import React, { useMemo, useCallback } from "react";
import {
  TreeSelect,
  Select,
  Input,
  InputNumber,
  Checkbox,
  DatePicker,
  Space,
} from "antd";
import { CodeOutlined, MailOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import TextArea from "antd/es/input/TextArea";
import "../../styles/components/DynamicFieldRenderer.css";
import TemplatePreview from "./TemplatePreview";
import TemplateVariableInput from "./TemplateVariableInput";
import EmailTemplateVariableInput from "./EmailTemplateVariableInput";

import { TimePicker } from "antd";

interface FilterConfig {
  dependsOnField: string; // Parent field name
  dependsOnDataKey: string; // Key in dynamicData to get parent options
  filterByKey: string; // Property in current field's options to filter by
  parentLinkKey: string; // Property in parent field's options to match against
}

interface FieldDefinition {
  name: string;
  label: string;
  type: string;
  dataSource?: string;
  dataSourceParams?: any;
  required?: boolean;
  defaultValue?: any;
  placeholder?: string;
  helpText?: string;
  dependsOn?: any;
  options?: any[];
  allowVariables?: boolean;
  supportsContextVariables?: boolean;
  filterConfig?: FilterConfig;
  previewConfig?: any;
}

interface DynamicFieldRendererProps {
  fields: FieldDefinition[];
  config: Record<string, any>;
  dynamicData: Record<string, any[]>;
  previousContextFields?: any[];
  onChange: (fieldName: string, value: any) => void;
}

export const DynamicFieldRenderer: React.FC<DynamicFieldRendererProps> = ({
  fields,
  config,
  dynamicData,
  previousContextFields = [],
  onChange,
}) => {
  // Memoize tree conversion
  const convertToTreeSelectFormat = useCallback((nodes: any[]): any[] => {
    if (!Array.isArray(nodes) || nodes.length === 0) return [];

    return nodes.map((node) => {
      const hasChildren =
        (node.children && node.children.length > 0) ||
        (node.subcategories && node.subcategories.length > 0);

      return {
        title: node.name || node.title || node.label,
        value: node.id || node.value,
        key: node.id || node.key || node.value,
        children: hasChildren
          ? convertToTreeSelectFormat(node.children || node.subcategories)
          : undefined,
      };
    });
  }, []);

  // Group context fields by category (memoized)
  const groupedContextFields = useMemo(() => {
    const groups: Record<string, any[]> = {};
    previousContextFields.forEach((field) => {
      const category = field.category || "Other";
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(field);
    });
    return groups;
  }, [previousContextFields]);

  /**
   * Combine static options with context variables
   */
  const getCombinedOptions = useCallback(
    (fieldName: string) => {
      const options: any[] = [];

      // Add static options from dynamicData or field.options
      const staticOptions = dynamicData[fieldName] || [];

      if (staticOptions.length > 0) {
        options.push({
          label: "📋 Available Options",
          options: staticOptions.map((opt: any) => ({
            value: opt.value || opt.id,
            label: opt.label || opt.name,
            type: "static",
          })),
        });
      }

      // Add context variables grouped by category
      Object.entries(groupedContextFields).forEach(([category, fields]) => {
        options.push({
          label: `🔗 ${category}`,
          options: fields.map((field: any) => ({
            value: `{{${field.key}}}`,
            label: `{{${field.key}}}`,
            // label: `${field.nodeLabel} → ${field.label}`,
            icon: field.icon,
            type: "variable",
          })),
        });
      });

      return options;
    },
    [dynamicData, groupedContextFields]
  );

  const isFieldVisible = useCallback(
    (field: FieldDefinition): boolean => {
      if (!field.dependsOn) return true;

      const dependentFieldName = field.dependsOn.field;
      const dependentValue = config[dependentFieldName];

      // Pattern 1: Check if value is NOT in the notIn array
      if (field.dependsOn.notIn?.includes(dependentValue)) {
        return false;
      }

      // Pattern 2: Check if value IS in the in array
      if (field.dependsOn.in && !field.dependsOn.in.includes(dependentValue)) {
        return false;
      }

      // Pattern 3: Check if field has specific value (single value match)
      if (
        field.dependsOn.value !== undefined &&
        dependentValue !== field.dependsOn.value
      ) {
        return false;
      }

      // Pattern 4: Check if field is not empty
      if (field.dependsOn.condition === "notEmpty" && !dependentValue) {
        return false;
      }

      // Pattern 4b: Check if field IS empty (inverse of notEmpty)
      if (field.dependsOn.condition === "empty" && dependentValue) {
        return false;
      }

      // Pattern 5: Check if value matches any in values array (multiple value match)
      if (field.dependsOn.values && Array.isArray(field.dependsOn.values)) {
        if (!field.dependsOn.values.includes(dependentValue)) {
          return false;
        }
      }

      // Pattern 6: Custom condition function (advanced use case)
      if (typeof field.dependsOn.condition === "function") {
        return field.dependsOn.condition(dependentValue, config);
      }

      return true;
    },
    [config]
  );

  // Generic function to filter options based on another field
  const getFilteredOptions = useCallback(
    (field: FieldDefinition) => {
      const rawOptions = dynamicData[field.name] || field.options || [];

      // If no filter configuration, return all options
      if (!field.filterConfig) {
        return rawOptions;
      }

      const { dependsOnField, dependsOnDataKey, filterByKey, parentLinkKey } =
        field.filterConfig;

      // Get the selected value from the parent field
      const parentValue = config[dependsOnField];

      // If no parent value selected, return empty array
      if (!parentValue) {
        return [];
      }
      // Get parent field's data to find the link value
      const parentOptions = dynamicData[dependsOnDataKey] || [];
      const selectedParent = parentOptions.find(
        (opt: any) => (opt.value || opt.id) === parentValue
      );
      // If parent not found or doesn't have the link key, return all
      if (!selectedParent || !selectedParent[parentLinkKey]) {
        return rawOptions;
      }

      // Filter current field's options based on the parent's link value
      const linkValue = selectedParent[parentLinkKey];
      return rawOptions.filter((opt: any) => opt[filterByKey] === linkValue);
    },
    [dynamicData, config]
  );

  const renderField = useCallback(
    (field: FieldDefinition, index: number) => {
      if (!isFieldVisible(field)) return null;

      const uniqueId = `${field.name}-${index}-${field.type}`;
      const value = config[field.name] ?? field.defaultValue ?? "";
      const allowVariables =
        field.allowVariables || field.supportsContextVariables;

      const commonProps = {
        key: uniqueId,
        id: uniqueId,
        className: "form-group",
      };

      switch (field.type) {
        case "string":
          return (
            <div {...commonProps}>
              <label className="form-label">
                {field.label}
                {field.required && <span className="required-star"> *</span>}
              </label>
              <Input
                value={value}
                onChange={(e) => onChange(field.name, e.target.value)}
                placeholder={
                  field.placeholder ||
                  (allowVariables ? "Type value or {{variable}}" : "")
                }
                prefix={<CodeOutlined style={{ color: "#bfbfbf" }} />}
                allowClear
                size="large"
              />
              {allowVariables && (
                <small className="form-hint-text">
                  💡 Type <code>{"{{"}</code> to use context variables
                </small>
              )}
              {field.helpText && (
                <small className="form-help-text">{field.helpText}</small>
              )}
            </div>
          );

        case "number":
          return (
            <div {...commonProps}>
              <label className="form-label">
                {field.label}
                {field.required && <span className="required-star"> *</span>}
              </label>
              <InputNumber
                value={value}
                onChange={(val) => onChange(field.name, val)}
                placeholder={field.placeholder}
                style={{ width: "100%" }}
                size="large"
              />
              {field.helpText && (
                <small className="form-help-text">{field.helpText}</small>
              )}
            </div>
          );

        case "email":
          return (
            <div {...commonProps}>
              <label className="form-label">
                {field.label}
                {field.required && <span className="required-star"> *</span>}
              </label>
              <Input
                type="email"
                value={value}
                onChange={(e) => onChange(field.name, e.target.value)}
                placeholder={field.placeholder || "user@example.com"}
                prefix={<MailOutlined style={{ color: "#bfbfbf" }} />}
                allowClear
                size="large"
              />
              {allowVariables && (
                <small className="form-hint-text">
                  💡 Supports context variables
                </small>
              )}
              {field.helpText && (
                <small className="form-help-text">{field.helpText}</small>
              )}
            </div>
          );

        case "textarea":
        case "richtext":
          return (
            <div {...commonProps}>
              <label className="form-label">
                {field.label}
                {field.required && <span className="required-star"> *</span>}
              </label>
              <TextArea
                value={value}
                onChange={(e) => onChange(field.name, e.target.value)}
                placeholder={
                  field.placeholder ||
                  (allowVariables ? "Type content or {{variable}}" : "")
                }
                rows={field.type === "richtext" ? 8 : 4}
                showCount
                maxLength={field.type === "richtext" ? 10000 : 5000}
              />
              {allowVariables && (
                <small className="form-hint-text">
                  💡 Type <code>{"{{"}</code> to use variables
                </small>
              )}
              {field.helpText && (
                <small className="form-help-text">{field.helpText}</small>
              )}
            </div>
          );

        case "boolean":
          return (
            <div {...commonProps}>
              <Checkbox
                checked={value === true}
                onChange={(e) => onChange(field.name, e.target.checked)}>
                <span className="checkbox-label">{field.label}</span>
              </Checkbox>
              {field.helpText && (
                <small className="form-help-text">{field.helpText}</small>
              )}
            </div>
          );

        case "checkbox":
          return (
            <div {...commonProps}>
              <Checkbox
                checked={value === true || value === "true"}
                onChange={(e) => onChange(field.name, e.target.checked)}>
                <span className="checkbox-label">
                  {field.label}
                  {field.required && <span className="required-star"> *</span>}
                </span>
              </Checkbox>
              {field.helpText && (
                <small
                  className="form-help-text"
                  style={{ display: "block", marginTop: "4px" }}>
                  {field.helpText}
                </small>
              )}
            </div>
          );

        case "date":
          return (
            <div {...commonProps}>
              <label className="form-label">
                {field.label}
                {field.required && <span className="required-star"> *</span>}
              </label>
              <DatePicker
                value={value ? dayjs(value) : null}
                onChange={(date) =>
                  onChange(field.name, date ? date.format("YYYY-MM-DD") : null)
                }
                placeholder={field.placeholder || "Select date"}
                style={{ width: "100%" }}
                size="large"
                allowClear
                getPopupContainer={(triggerNode) =>
                  triggerNode.parentElement || document.body
                }
              />
              {field.helpText && (
                <small className="form-help-text">{field.helpText}</small>
              )}
            </div>
          );

        case "datetime":
          return (
            <div {...commonProps}>
              <label className="form-label">
                {field.label}
                {field.required && <span className="required-star"> *</span>}
              </label>
              <DatePicker
                showTime
                value={value && value !== "" ? dayjs(value) : null}
                onChange={(date) => {
                  if (date) {
                    onChange(field.name, date.toISOString());
                  } else {
                    onChange(field.name, null);
                  }
                }}
                placeholder={field.placeholder || "Select date & time"}
                style={{ width: "100%" }}
                format="YYYY-MM-DD HH:mm:ss"
                size="large"
                allowClear
                getPopupContainer={(triggerNode) =>
                  triggerNode.parentElement || document.body
                }
              />
              {allowVariables && (
                <small className="form-hint-text">
                  💡 Supports context variables
                </small>
              )}
              {field.helpText && (
                <small className="form-help-text">{field.helpText}</small>
              )}
            </div>
          );

        case "time-24":
          return (
            <TimeField
              key={uniqueId}
              field={field}
              value={value}
              onChange={onChange}
              use12Hours={false}
            />
          );

        case "time-12":
          return (
            <TimeField
              key={uniqueId}
              field={field}
              value={value}
              onChange={onChange}
              use12Hours={true}
            />
          );

        case "select":
          return renderSelectField(field, value, allowVariables);

        case "multiselect":
          return renderMultiSelectField(field, value, allowVariables);

        case "multiselecttree":
          return renderMultiSelectTreeField(field, value);

        case "tags":
          if (field.name === "template_variables") {
            const selectedTemplate = dynamicData["provider_template"]?.find(
              (t: any) => t.value === config["provider_template"]
            );

            return (
              <TemplateVariableInput
                template={selectedTemplate}
                value={config.template_variables || []}
                onChange={(vars: any) => onChange("template_variables", vars)}
                previousContextFields={previousContextFields}
              />
            );
          }
          return renderTagsField(field, value);

        case "json":
          if (field.name === "template_variables" && config["template_id"]) {
            const selectedEmailTemplate = dynamicData["template_id"]?.find(
              (t: any) => t.value === config["template_id"]
            );
            const currentVars =
              value && typeof value === "object" && !Array.isArray(value)
                ? value
                : {};
            return (
              <EmailTemplateVariableInput
                template={selectedEmailTemplate}
                value={currentVars}
                onChange={(vars) => onChange("template_variables", vars)}
                previousContextFields={previousContextFields}
              />
            );
          }
          return renderJsonField(field, value);

        case "read-only-text":
          if (field.previewConfig?.displayFormat === "template") {
            const selectedTemplate = dynamicData[
              field.previewConfig.sourceField
            ]?.find(
              (t: any) => t.value === config[field.previewConfig.sourceField]
            );

            return (
              <TemplatePreview
                template={selectedTemplate}
                variables={config.template_variables || []}
              />
            );
          }
          return null;

        default:
          return null;
      }
    },
    [config, isFieldVisible, onChange, getCombinedOptions]
  );

  // Select Field Renderer
  const renderSelectField = useCallback(
    (
      field: FieldDefinition,
      value: any,
      allowVariables: boolean | undefined
    ) => {
      const isContextFieldSelect = field.dataSource === "contextfields";
      // Check if field has dependent filtering
      const hasFiltering = !!field.filterConfig;
      const parentField = field.filterConfig?.dependsOnField;
      const parentValue = parentField ? config[parentField] : null;
      const isDisabled = hasFiltering && !parentValue;

      // Use generic filtering - works for ANY field with filterConfig
      const filteredOptions = hasFiltering
        ? getFilteredOptions(field)
        : dynamicData[field.name] || field.options || [];

      // If allowVariables, show combined dropdown (static + context variables)
      if (allowVariables && !isContextFieldSelect) {
        return (
          <div key={field.name} className="form-group">
            <label className="form-label">
              {field.label}
              {field.required && <span className="required-star"> *</span>}
            </label>
            <Select
              className="custom-select"
              style={{ width: "100%" }}
              placeholder={
                isDisabled
                  ? `Please select ${parentField} first`
                  : field.placeholder || `Select ${field.label}`
              }
              value={value || undefined}
              onChange={(selectedValue) => onChange(field.name, selectedValue)}
              disabled={isDisabled}
              allowClear
              showSearch
              size="large"
              options={getCombinedOptions(field.name)}
              optionRender={(option) => (
                <Space>
                  {option.data?.type === "variable" && (
                    <span className="variable-badge">VAR</span>
                  )}
                  <span>{option.label}</span>
                </Space>
              )}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toString()
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              getPopupContainer={(triggerNode) =>
                triggerNode.parentElement || document.body
              }
            />
            {isDisabled && (
              <small className="form-hint-text" style={{ color: "#ff4d4f" }}>
                ⚠️ Select {parentField} first to see available options
              </small>
            )}
            {field.helpText && !isDisabled && (
              <small className="form-help-text">{field.helpText}</small>
            )}
          </div>
        );
      }

      // Regular select without variables - USE ANTD SELECT INSTEAD OF CommonSelect
      const selectOptions = isContextFieldSelect
        ? previousContextFields.map((ctx: any) => ({
            value: ctx.key,
            label: `{{${ctx.key}}}`,
          }))
        : filteredOptions.map((opt: any) => ({
            value: opt.value || opt.id,
            label: opt.label || opt.name,
          }));

      return (
        <div key={field.name} className="form-group">
          <label className="form-label">
            {field.label}
            {field.required && <span className="required-star"> *</span>}
          </label>
          <Select
            className="custom-select"
            style={{ width: "100%" }}
            placeholder={
              isDisabled
                ? `Please select ${parentField} first`
                : field.placeholder || `Select ${field.label}`
            }
            value={value || undefined}
            onChange={(selectedValue) => onChange(field.name, selectedValue)}
            disabled={isDisabled}
            allowClear
            showSearch
            size="large"
            options={selectOptions}
            filterOption={(input, option) =>
              (option?.label ?? "")
                .toString()
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            getPopupContainer={(triggerNode) =>
              triggerNode.parentElement || document.body
            }
          />
          {isDisabled && (
            <small className="form-hint-text" style={{ color: "#ff4d4f" }}>
              ⚠️ Select {parentField} first to see available options
            </small>
          )}
          {field.helpText && !isDisabled && (
            <small className="form-help-text">{field.helpText}</small>
          )}
        </div>
      );
    },
    [
      dynamicData,
      previousContextFields,
      getCombinedOptions,
      onChange,
      getFilteredOptions,
      config,
    ]
  );

  // Multi-Select Field Renderer
  const renderMultiSelectField = useCallback(
    (
      field: FieldDefinition,
      value: any,
      allowVariables: boolean | undefined
    ) => {
      // Use generic filtering - works for ANY field with filterConfig
      const filteredOptions = getFilteredOptions(field);
      const multiselectOptions = filteredOptions;
      const multiValue = Array.isArray(value) ? value : value ? [value] : [];

      // Check if field has dependent filtering
      const hasFiltering = !!field.filterConfig;
      const parentField = field.filterConfig?.dependsOnField;
      const parentValue = parentField ? config[parentField] : null;
      const isDisabled = hasFiltering && !parentValue;

      // If allowVariables, show combined dropdown (static + context variables)
      if (allowVariables) {
        return (
          <div key={field.name} className="form-group">
            <label className="form-label">
              {field.label}
              {field.required && <span className="required-star"> *</span>}
            </label>
            <Select
              mode="multiple"
              className="custom-multiselect"
              style={{ width: "100%" }}
              placeholder={
                isDisabled
                  ? `Please select ${parentField} first`
                  : field.placeholder || `Select ${field.label}`
              }
              value={multiValue}
              onChange={(selectedValues) =>
                onChange(field.name, selectedValues || [])
              }
              disabled={isDisabled}
              allowClear
              showSearch
              maxTagCount="responsive"
              size="large"
              options={getCombinedOptions(field.name)}
              optionRender={(option) => (
                <Space>
                  {option.data?.type === "variable" && (
                    <span className="variable-badge">VAR</span>
                  )}
                  <span>{option.label}</span>
                </Space>
              )}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toString()
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              getPopupContainer={(triggerNode) =>
                triggerNode.parentElement || document.body
              }
            />
            <small className="form-hint-text">
              💡 Mix static options with context variables
            </small>
          </div>
        );
      }

      // Regular multiselect without variables
      return (
        <div key={field.name} className="form-group">
          <label className="form-label">
            {field.label}
            {field.required && <span className="required-star"> *</span>}
          </label>
          <Select
            mode="multiple"
            className="custom-multiselect"
            style={{ width: "100%" }}
            placeholder={
              isDisabled
                ? `Please select ${parentField} first`
                : field.placeholder || `Select ${field.label}`
            }
            value={multiValue}
            onChange={(selectedValues) =>
              onChange(field.name, selectedValues || [])
            }
            disabled={isDisabled}
            allowClear
            showSearch
            maxTagCount="responsive"
            size="large"
            options={multiselectOptions.map((opt: any) => ({
              value: opt.value || opt.id,
              label: opt.label || opt.name,
            }))}
            getPopupContainer={(triggerNode) =>
              triggerNode.parentElement || document.body
            }
          />
          {isDisabled && (
            <small className="form-hint-text" style={{ color: "#ff4d4f" }}>
              ⚠️ Select {parentField} first to see available options
            </small>
          )}
          {field.helpText && !isDisabled && (
            <small className="form-help-text">{field.helpText}</small>
          )}
        </div>
      );
    },
    [dynamicData, getCombinedOptions, onChange, getFilteredOptions, config]
  );

  // Multi-Select Tree Field Renderer (unchanged - no combined mode)
  const renderMultiSelectTreeField = useCallback(
    (field: FieldDefinition, value: any) => {
      const rawTreeData = dynamicData[field.name] || field.options || [];
      const categoryTreeData = convertToTreeSelectFormat(rawTreeData);
      const treeValue = Array.isArray(value) ? value : value ? [value] : [];

      return (
        <div key={field.name} className="form-group">
          <label className="form-label">
            {field.label}
            {field.required && <span className="required-star"> *</span>}
          </label>
          <TreeSelect
            multiple
            treeCheckable
            showCheckedStrategy={TreeSelect.SHOW_CHILD}
            treeCheckStrictly={false}
            showSearch
            className="custom-tree-select"
            treeNodeFilterProp="title"
            style={{ width: "100%" }}
            value={treeValue}
            dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
            treeData={categoryTreeData}
            placeholder={field.placeholder || "Select categories"}
            allowClear
            maxTagCount="responsive"
            treeDefaultExpandAll={false}
            size="large"
            onChange={(selectedValues) =>
              onChange(field.name, selectedValues || [])
            }
            getPopupContainer={(triggerNode) =>
              triggerNode.parentElement || document.body
            }
          />
          {field.helpText && (
            <small className="form-help-text">{field.helpText}</small>
          )}
        </div>
      );
    },
    [dynamicData, convertToTreeSelectFormat, onChange]
  );

  // Tags Field Renderer
  const renderTagsField = useCallback(
    (field: FieldDefinition, value: any) => {
      const tagsValue = Array.isArray(value) ? value : value ? [value] : [];

      return (
        <div key={field.name} className="form-group">
          <label className="form-label">
            {field.label}
            {field.required && <span className="required-star"> *</span>}
          </label>
          <Select
            mode="tags"
            className="custom-tags-select"
            style={{ width: "100%" }}
            placeholder={field.placeholder || "Add tags"}
            value={tagsValue}
            onChange={(selectedValues) =>
              onChange(field.name, selectedValues || [])
            }
            allowClear
            maxTagCount="responsive"
            size="large"
            tokenSeparators={[","]}
            getPopupContainer={(triggerNode) =>
              triggerNode.parentElement || document.body
            }
          />
          {field.helpText && (
            <small className="form-help-text">{field.helpText}</small>
          )}
        </div>
      );
    },
    [onChange]
  );

  // JSON Field Renderer
  const renderJsonField = useCallback(
    (field: FieldDefinition, value: any) => {
      const jsonValue =
        typeof value === "string" ? value : JSON.stringify(value, null, 2);

      return (
        <div key={field.name} className="form-group">
          <label className="form-label">
            {field.label}
            {field.required && <span className="required-star"> *</span>}
          </label>
          <TextArea
            value={jsonValue}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                onChange(field.name, parsed);
              } catch {
                // If not valid JSON, store as string
                onChange(field.name, e.target.value);
              }
            }}
            placeholder={field.placeholder || '{"key": "value"}'}
            rows={6}
            className="json-textarea"
            style={{ fontFamily: "monospace" }}
          />
          {field.helpText && (
            <small className="form-help-text">{field.helpText}</small>
          )}
        </div>
      );
    },
    [onChange]
  );

  const TimeField = ({
    field,
    value,
    onChange,
    use12Hours,
    placeholder,
  }: any) => {
    const displayFormat = use12Hours ? "h:mm A" : "HH:mm";

    return (
      <div className="form-group">
        <label className="form-label">
          {field?.label}
          {field?.required && <span className="required-star"> *</span>}
        </label>
        <TimePicker
          value={value ? dayjs(value, displayFormat) : null}
          onChange={(time) => onChange(field?.name, time?.format("HH:mm:ss"))}
          format={displayFormat}
          use12Hours={use12Hours}
          placeholder={
            placeholder ||
            field?.placeholder ||
            (use12Hours ? "Select time (12h)" : "Select time (24h)")
          }
          style={{ width: "100%" }}
          size="large"
          allowClear
          getPopupContainer={(triggerNode) =>
            triggerNode.parentElement || document.body
          }
        />
        {field?.helpText && (
          <small className="form-help-text">{field.helpText}</small>
        )}
      </div>
    );
  };

  // Early return AFTER all hooks
  if (!fields || fields.length === 0) {
    return (
      <div className="form-group empty-fields">
        <p>No configuration fields available for this node.</p>
      </div>
    );
  }

  return <>{fields.map((field, index) => renderField(field, index))}</>;
};

export default DynamicFieldRenderer;
